import {Col, Form, Row, Select} from 'antd';
import {useEffect, useState} from 'react';
import {useQuery} from 'react-query';
import api from '../../api';
import MeetingArchiveToggle from '../../components/MeetingArchiveToggle';

const MemberSearch = ({searchParams = {}, setSearchParams}) => {

  const [form] = Form.useForm();
  const [showArchived, setShowArchived] = useState(false);
  const isBuevMemberMeeting = (meeting) => {
    const name = (meeting?.name || '').toLowerCase();
    return name.includes('mitgliederversammlung') && (name.includes('büv') || name.includes('buev'));
  };

  const {data: {data: meetings = []} = {}} = useQuery(
    ['search-member-meeting', showArchived],
    () => {
      return api.meetings.meetingList(showArchived ? {archive_only: true} : {});
    }, {
      cacheTime: Infinity,
      onSuccess: ({data}) => {
        if (!data.length) {
          return;
        }
        if (searchParams.meeting && data.some(item => String(item.id) === String(searchParams.meeting))) {
          form.setFieldsValue({
            meeting: searchParams.meeting,
            status: searchParams.status,
            response_status: searchParams.response_status,
            state: searchParams.state,
            special_program: searchParams.special_program,
            evening_program: searchParams.evening_program,
            accompany_program: searchParams.accompany_program,
          });
          return;
        }
        const {id: value} = data[0];
        setSearchParams({...searchParams, meeting: value});
        form.setFieldsValue({
          meeting: data[0].id
        });
      }
    });

  const toggleArchived = (value) => {
    setShowArchived(value);
    setSearchParams({});
    form.resetFields();
  };

  const {data: {data: states = []} = {}} = useQuery(
    ['search-member-state'],
    () => {
      return api.meetings.stateList();
    }, {
      cacheTime: Infinity,
    });

  const {data: {data: statuses = []} = {}} = useQuery(
    ['search-member-status'],
    () => {
      return api.meetings.statusList();
    }, {
      cacheTime: Infinity,
    });

  const {data: {data: specialPrograms = []} = {}} = useQuery(
    ['search-special_program', searchParams.meeting],
    () => {
      return api.meetings.meetingSpecialProgramList({meeting: searchParams.meeting});
    }, {
      cacheTime: Infinity,
      enabled: !!searchParams.meeting,
    });

  const {data: {data: eveningPrograms = []} = {}} = useQuery(
    ['search-evening_program', searchParams.meeting],
    () => {
      return api.meetings.meetingEveningProgramList({meeting: searchParams.meeting});
    }, {
      cacheTime: Infinity,
      enabled: !!searchParams.meeting,
    });

  const {data: {data: accompanyPrograms = []} = {}} = useQuery(
    ['search-accompany_program', searchParams.meeting],
    () => {
      return api.meetings.meetingAccompanyProgramList({meeting: searchParams.meeting});
    }, {
      cacheTime: Infinity,
      enabled: !!searchParams.meeting,
    });


  const paramChangeHandler = (param) => (value) => {
    const nextParams = {...searchParams, [param]: value};
    if (param === 'meeting') {
      const nextMeeting = meetings.find((item) => item.id === value);
      if (isBuevMemberMeeting(nextMeeting)) {
        delete nextParams.state;
        delete nextParams.special_program;
        delete nextParams.accompany_program;
        form.resetFields(['state', 'special_program', 'accompany_program']);
      }
    }
    setSearchParams(nextParams);
    return undefined;
  }
  const selectedMeeting = meetings.find((item) => item.id === searchParams.meeting);
  const isBuevAdminView = isBuevMemberMeeting(selectedMeeting);

  useEffect(() => {
    if (!isBuevAdminView) {
      return;
    }
    const hiddenFields = ['state', 'special_program', 'accompany_program'];
    const hasHiddenSearchParams = hiddenFields.some((name) => searchParams[name]);
    if (hasHiddenSearchParams) {
      const nextParams = {...searchParams};
      hiddenFields.forEach((name) => delete nextParams[name]);
      setSearchParams(nextParams);
      form.resetFields(hiddenFields);
    }
  }, [form, isBuevAdminView, searchParams, setSearchParams]);

  return <>
  <div className="mb-20">
    <MeetingArchiveToggle showArchived={showArchived} onChange={toggleArchived}/>
  </div>
  <Form
    form={form}
    name="search"
    layout="vertical"
  >
    <Row gutter={16}>
      <Col xs={24} md={12}>
        <Form.Item label="Tagung" name="meeting">
          <Select
            style={{width: '100%'}}
            value={searchParams && searchParams.meeting}
            onChange={paramChangeHandler('meeting')}
          >
            {meetings.map(item => <Select.Option key={item.id} value={item.id}>
              {item.name}
            </Select.Option>)}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item label="Teilnehmerstatus" name="status">
          <Select
            style={{width: '100%'}}
            value={searchParams && searchParams.status}
            onChange={paramChangeHandler('status')}
            allowClear={true}
          >
            {statuses.map(item => <Select.Option key={item.id} value={item.id}>
              {item.name}
            </Select.Option>)}
          </Select>
        </Form.Item>
      </Col>
    </Row>
    <Row gutter={16}>
      <Col xs={24} md={12}>
        <Form.Item label="Rückmeldung" name="response_status">
          <Select
            style={{width: '100%'}}
            value={searchParams && searchParams.response_status}
            onChange={paramChangeHandler('response_status')}
            allowClear={true}
          >
            <Select.Option value="confirmed">Teilnahme bestätigt</Select.Option>
            <Select.Option value="declined">Abgesagt</Select.Option>
            <Select.Option value="waitlist">Warteliste</Select.Option>
            <Select.Option value="open">Noch keine Rückmeldung</Select.Option>
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item label="Abendprogramm" name="evening_program">
          <Select
            style={{width: '100%'}}
            value={searchParams && searchParams.evening_program}
            onChange={paramChangeHandler('evening_program')}
            allowClear={true}
          >
            {eveningPrograms.map(item => <Select.Option key={item.id} value={item.id}>
              {item.name}
            </Select.Option>)}
          </Select>
        </Form.Item>
      </Col>
    </Row>
    {!isBuevAdminView && <Row gutter={16}>
      <Col xs={24} md={12}>
        <Form.Item label="Bundesland" name="state">
          <Select
            style={{width: '100%'}}
            value={searchParams && searchParams.state}
            onChange={paramChangeHandler('state')}
            allowClear={true}
          >
            {states.map(item => <Select.Option key={item.id} value={item.id}>
              {item.name}
            </Select.Option>)}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item label="Fachprogramm" name="special_program">
          <Select
            style={{width: '100%'}}
            value={searchParams && searchParams.special_program}
            onChange={paramChangeHandler('special_program')}
            allowClear={true}
          >
            {specialPrograms.map(item => <Select.Option key={item.id} value={item.id}>
              {item.name}
            </Select.Option>)}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item label="Begleitprogramm" name="accompany_program">
          <Select
            style={{width: '100%'}}
            value={searchParams && searchParams.accompany_program}
            onChange={paramChangeHandler('accompany_program')}
            allowClear={true}
          >
            {accompanyPrograms.map(item => <Select.Option key={item.id} value={item.id}>
              {item.name}
            </Select.Option>)}
          </Select>
        </Form.Item>
      </Col>
    </Row>}
  </Form>
  </>;
}

export default MemberSearch;
